import React, { useState, useEffect, useCallback } from 'react';
import { studentApi } from '../../services/api';
import { Student } from '../../types';
import StudentForm from './StudentForm';
import { handleApiError, handleSuccess } from '../../utils/errorHandler';
import './StudentList.css';

const StudentList: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await studentApi.getAll({
        search: searchTerm.trim() || undefined,
        active: activeFilter,
        limit: 50
      });
      
      if (result && result.data) {
        setStudents(result.data.students);
      }
    } catch (err: any) {
      setError(err.error || 'Failed to load students');
      handleApiError(err, 'Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, activeFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadStudents();
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
  }, [loadStudents]);

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setShowForm(true);
  };

  const handleDelete = async (student: Student) => {
    const isActive = student.is_active;
    const action = isActive ? 'deactivate' : 'permanently delete';
    const studentName = `${student.first_name} ${student.last_name}`;
    const confirmMessage = isActive 
      ? `Are you sure you want to deactivate ${studentName}? They will be moved to inactive status.`
      : `Are you sure you want to permanently delete ${studentName}? This action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        // Pass force=true when permanently deleting an inactive student
        await studentApi.delete(student.id, !isActive);
        const successMessage = isActive 
          ? 'Student deactivated successfully' 
          : 'Student permanently deleted';
        handleSuccess(successMessage);
        loadStudents();
      } catch (error: any) {
        handleApiError(error, `Failed to ${action} student`);
      }
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingStudent(null);
    // Clear search and reset filters to show all students including the new one
    setSearchTerm('');
    setActiveFilter(true);
    // Small delay to ensure the form state is cleared before reloading
    setTimeout(() => {
      loadStudents();
    }, 100);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingStudent(null);
  };

  const filteredStudents = students;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading students...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading students: {error}</p>
        <button onClick={loadStudents} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="student-list">
      <div className="student-list-header">
        <h2>Students</h2>
        <button 
          className="add-button"
          onClick={() => setShowForm(true)}
        >
          Add Student
        </button>
      </div>

      <div className="student-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-container">
          <label>
            <input
              type="radio"
              name="activeFilter"
              checked={activeFilter === true}
              onChange={() => setActiveFilter(true)}
            />
            Active Only
          </label>
          <label>
            <input
              type="radio"
              name="activeFilter"
              checked={activeFilter === undefined}
              onChange={() => setActiveFilter(undefined)}
            />
            All
          </label>
        </div>
      </div>

      <div className="student-grid">
        {filteredStudents.map((student) => (
          <div key={student.id} className="student-card">
            <div className="student-header">
              <div className="student-info">
                <h3>{student.first_name} {student.last_name}</h3>
                <p className="emirates-id">Emirates ID: {student.emirates_id}</p>
                <p className="contact">Contact: {student.primary_contact}</p>
              </div>
              <div className={`status-badge ${student.is_active ? 'active' : 'inactive'}`}>
                {student.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
            
            <div className="student-details">
              {student.therapy_needs && (
                <div className="detail-section">
                  <strong>Therapy Needs:</strong>
                  <p>{student.therapy_needs}</p>
                </div>
              )}
              {student.special_requirements && (
                <div className="detail-section">
                  <strong>Special Requirements:</strong>
                  <p>{student.special_requirements}</p>
                </div>
              )}
            </div>

            <div className="student-actions">
              <button 
                className="edit-button"
                onClick={() => handleEdit(student)}
              >
                Edit
              </button>
              <button 
                className={`delete-button ${student.is_active ? 'deactivate' : 'permanent-delete'}`}
                onClick={() => handleDelete(student)}
              >
                {student.is_active ? 'Deactivate' : 'Delete'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredStudents.length === 0 && (
        <div className="empty-state">
          <p>No students found.</p>
          <button 
            className="add-button"
            onClick={() => setShowForm(true)}
          >
            Add First Student
          </button>
        </div>
      )}

      {showForm && (
        <StudentForm
          student={editingStudent}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default StudentList;